import type {
  AudienceSegmentTagGroup,
  CumulativeBundleSegment,
  CumulativeDomainSegment,
  InventoryBundle,
  InventoryDomain,
} from '@openx/types/gqlTypes';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import { format } from 'date-fns';
import type { TFunction } from 'i18next';
import isPlainObject from 'lodash/isPlainObject';
import { type DealPromptContextType, dealPromptContextTypes } from '../../../utils/types';

const EXPORT_CONFIG_PER_CONTEXT = {
  [dealPromptContextTypes.DOMAINS]: {
    itemsOnlyPrefix: 'Domains Export',
    fullDatasetPrefix: 'Domains Export - Full Dataset',
    preferredKeys: ['domainName', 'domain', 'domainId', 'id'],
    itemNameExtractor: (d: InventoryDomain) => d?.domainName || '',
  },
  [dealPromptContextTypes.APPS]: {
    itemsOnlyPrefix: 'Apps Export',
    fullDatasetPrefix: 'Apps Export - Full Dataset',
    preferredKeys: ['appNme', 'bundleId', 'appId', 'appInfoContentRating', 'id', 'domainName', 'domain'],
    itemNameExtractor: (b: InventoryBundle) => b?.bundleId || '',
  },
} as const;

const csvEscape = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toHeader = (key: string): string => {
  return key
    .split('.')
    .map(part => {
      return part
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, c => c.toUpperCase());
    })
    .join(' / ');
};

/**
 * Flattens a deeply nested object into dot-notation keys.
 */
const flatten = (obj: unknown, prefix = '', out: Record<string, unknown> = {}): Record<string, unknown> => {
  if (obj == null) return out;

  if (Array.isArray(obj)) {
    const key = prefix.replace(/\.$/, '');
    const joined = obj
      .map(value => {
        if (isPlainObject(value)) return JSON.stringify(value);
        return String(value ?? '');
      })
      .join('; ');
    out[key] = joined;
    return out;
  }

  if (!isPlainObject(obj)) {
    const key = prefix.replace(/\.$/, '');
    out[key] = obj as unknown;
    return out;
  }

  for (const key of Object.keys(obj)) {
    const val = (obj as Record<string, unknown>)[key];
    const next = prefix ? `${prefix}.${key}` : key;
    flatten(val, next, out);
  }

  return out;
};

export const readStringProp = <T extends Record<string, unknown>>(
  obj: T | null | undefined,
  keys: readonly string[]
): string => {
  if (!obj) return '';
  for (const key of keys) {
    if (Object.hasOwn(obj, key)) {
      const v = obj[key];
      if (typeof v === 'string') return v;
    }
  }
  return '';
};

export const getItemList = ({
  item,
  selectedItems,
  contextType,
}: {
  item: CumulativeDomainSegment | CumulativeBundleSegment;
  selectedItems: InventoryDomain[] | InventoryBundle[];
  contextType: DealPromptContextType;
}): string[] => {
  const hasSelection = selectedItems.length > 0;
  const items = hasSelection ? selectedItems : item.results;
  const extractor = EXPORT_CONFIG_PER_CONTEXT[contextType].itemNameExtractor as (i: any) => string;
  return items?.map(extractor) || [];
};

export const formatItemForItemsOnlyCsvDownload = ({
  item,
  selectedItems,
  marketRequestsLabel,
  impressionsLabel,
  contextType,
}: {
  item: CumulativeDomainSegment | CumulativeBundleSegment;
  selectedItems: InventoryDomain[] | InventoryBundle[];
  marketRequestsLabel: string;
  impressionsLabel: string;
  contextType: DealPromptContextType;
}): string[] => {
  const hasSelection = selectedItems.length > 0;
  const items = getItemList({ item, selectedItems, contextType });

  const marketRequests = hasSelection
    ? selectedItems.reduce((sum, singleItem) => sum + singleItem.totalCprMarketRequests, 0)
    : item.metrics.totalCprMarketRequests;

  const impressions = hasSelection
    ? selectedItems.reduce((sum, singleItem) => sum + singleItem.totalImpressions, 0)
    : item.metrics.totalImpressions;

  const marketRequestsAbbrev = abbreviateNumericValue(marketRequests, { decimalPlaces: 2 });
  const impressionsAbbrev = abbreviateNumericValue(impressions, { decimalPlaces: 2 });

  return [`${marketRequestsLabel} ${marketRequestsAbbrev}`, `${impressionsLabel} ${impressionsAbbrev}`, ...items];
};

export const getItemsOnlyCsvFilename = (contextType: DealPromptContextType): string => {
  const formattedDate = format(new Date(), 'yyyy-MM-dd HH.mm.ss');
  const prefix = EXPORT_CONFIG_PER_CONTEXT[contextType].itemsOnlyPrefix;
  return `${prefix} - ${formattedDate}`;
};

export const formatItemForFullListCsvDownload = ({
  item,
  selectedItems,
  contextType,
}: {
  item: CumulativeDomainSegment | CumulativeBundleSegment;
  selectedItems: InventoryDomain[] | InventoryBundle[];
  contextType: DealPromptContextType;
}): string[] => {
  const hasSelection = selectedItems.length > 0;
  const items = hasSelection ? selectedItems : item.results;

  const flattened =
    items?.map(singleItem => {
      return flatten(singleItem);
    }) ?? [];

  const preferred = EXPORT_CONFIG_PER_CONTEXT[contextType].preferredKeys;
  const allKeys = new Set<string>();

  for (const row of flattened) {
    for (const key of Object.keys(row)) {
      allKeys.add(key);
    }
  }

  const remaining = Array.from(allKeys).filter(keyString => !preferred.includes(keyString));
  remaining.sort((a, b) => a.localeCompare(b));

  const ordered = [...preferred.filter(keyString => allKeys.has(keyString)), ...remaining];

  const headerLine = ordered.map(key => csvEscape(toHeader(key))).join(',');
  const dataLines = flattened.map(row => ordered.map(key => csvEscape(row[key] ?? '')).join(','));

  return [headerLine, ...dataLines];
};

export const getFullDataSetCsvFilename = (contextType: DealPromptContextType): string => {
  const formattedDate = format(new Date(), 'yyyy-MM-dd HH.mm.ss');
  const prefix = EXPORT_CONFIG_PER_CONTEXT[contextType].fullDatasetPrefix;
  return `${prefix} - ${formattedDate}`;
};

export const getParagraphs = (t: TFunction) => [
  {
    title: t('1. Only OpenXSelect taxonomy segments'),
    description: t('All selected segments are from the predefined OpenX taxonomy'),
  },
  {
    title: t('2. Only segments from a single data partner'),
    description: t('All selected non-OpenX segments share the same account_id.'),
  },
  {
    title: t('3. OpenXSelect taxonomy + segments from one data partner'),
    description: t('A mix of predefined OpenX taxonomy segments and segments coming from one data partner.'),
  },
  {
    title: t('4. Only public Data Marketplace segments'),
    description: t('All selected segments are public.'),
  },
  {
    title: t('5. OpenXSelect taxonomy + public segments'),
    description: t('A mix of predefined OpenX taxonomy segments and public segments.'),
  },
  {
    title: t('6. OpenXSelect taxonomy + public segments + segments from one data partner'),
    description: t(
      'Allowed if all non-public, non-OpenX segments come from one data partner. OpenX taxonomy and public segments may be included.'
    ),
  },
];

export const combineTagsIntoName = (groups: AudienceSegmentTagGroup[]) => {
  return groups.reduce((acc, group) => {
    return acc.length > 0 ? `${acc}_${group.tags.join('_')}` : acc + group.tags.join('_');
  }, '');
};
